import React, { useState, useRef, useEffect } from 'react';
import Icon from './AppIcon';

// ── Date preset helpers ───────────────────────────────────────
export const getDateRange = (preset, customFrom, customTo) => {
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
  const endOf   = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);

  switch (preset) {
    case 'today':
      return { from: startOf(today), to: endOf(today) };

    case 'yesterday': {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      return { from: startOf(y), to: endOf(y) };
    }

    case 'this_month':
      return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: endOf(today) };

    case 'last_month': {
      const lmStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lmEnd   = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: startOf(lmStart), to: endOf(lmEnd) };
    }

    case 'last_30': {
      const l30 = new Date(today); l30.setDate(l30.getDate() - 30);
      return { from: startOf(l30), to: endOf(today) };
    }

    case 'this_fy': {
      // India FY: April 1 to March 31
      const fyStart = today.getMonth() >= 3
        ? new Date(today.getFullYear(), 3, 1)
        : new Date(today.getFullYear() - 1, 3, 1);
      return { from: startOf(fyStart), to: endOf(today) };
    }

    case 'custom':
      return {
        from: customFrom ? new Date(customFrom + 'T00:00:00') : null,
        to:   customTo   ? new Date(customTo   + 'T23:59:59') : null,
      };

    case 'till_date':
    default:
      return { from: null, to: null };
  }
};

export const PRESETS = [
  { key: 'this_fy',    label: 'This FY' },
  { key: 'till_date',  label: 'Till Date' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'last_30',    label: 'Last 30 Days' },
  { key: 'today',      label: 'Today' },
  { key: 'yesterday',  label: 'Yesterday' },
  { key: 'custom',     label: 'Custom Range' },
];

const formatRangeLabel = (preset, customFrom, customTo) => {
  const fmt = (d) => d?.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) || '';
  const { from, to } = getDateRange(preset, customFrom, customTo);
  if (!from && !to) return 'All Time';
  if (from && to) return `${fmt(from)} – ${fmt(to)}`;
  return 'All Time';
};

// ── Component ─────────────────────────────────────────────────
// Props:
//   preset      — current preset key (string)
//   customFrom  — custom from date string (YYYY-MM-DD)
//   customTo    — custom to date string (YYYY-MM-DD)
//   onPresetChange   — (key) => void
//   onCustomFromChange — (val) => void
//   onCustomToChange   — (val) => void
const DateRangeFilter = ({
  preset = 'till_date',
  customFrom = '',
  customTo = '',
  onPresetChange,
  onCustomFromChange,
  onCustomToChange,
}) => {
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const presetLabel = PRESETS.find(p => p.key === preset)?.label || 'Till Date';
  const rangeLabel  = formatRangeLabel(preset, customFrom, customTo);

  return (
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div>
        <p className="text-xs font-medium" style={{ color: '#6b7280' }}>Showing data for</p>
        <p className="text-sm font-bold" style={{ color: '#1f2937' }}>{rangeLabel}</p>
      </div>
      <div className="relative" ref={dropRef}>
        <button
          onClick={() => setDropOpen(v => !v)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border bg-white text-sm font-semibold shadow-sm transition hover:shadow-md"
          style={{ borderColor: '#1e4a72', color: '#1e4a72' }}
        >
          <Icon name="Calendar" size={15} />
          {presetLabel}
          <Icon name={dropOpen ? 'ChevronUp' : 'ChevronDown'} size={14} />
        </button>

        {dropOpen && (
          <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border z-50 overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
            {PRESETS.map(p => (
              <button
                key={p.key}
                onClick={() => { onPresetChange?.(p.key); if (p.key !== 'custom') setDropOpen(false); }}
                className="w-full text-left px-4 py-2.5 text-sm transition hover:bg-amber-50"
                style={{
                  backgroundColor: preset === p.key ? '#1e4a72' : 'transparent',
                  color:           preset === p.key ? '#fff'    : '#374151',
                  fontWeight:      preset === p.key ? '600'     : '400',
                }}
              >
                {p.label}
              </button>
            ))}
            {preset === 'custom' && (
              <div className="px-4 pb-3 pt-1 space-y-2 border-t" style={{ borderColor: '#f3f4f6' }}>
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: '#6b7280' }}>From</label>
                  <input type="date" value={customFrom} onChange={e => onCustomFromChange?.(e.target.value)}
                    className="w-full text-xs px-2 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-amber-400"
                    style={{ borderColor: '#d1d5db' }} />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: '#6b7280' }}>To</label>
                  <input type="date" value={customTo} onChange={e => onCustomToChange?.(e.target.value)}
                    className="w-full text-xs px-2 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-amber-400"
                    style={{ borderColor: '#d1d5db' }} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangeFilter;
